import { createSelector } from '@ngrx/store';
import { parseISO } from 'date-fns';

import { CandidateSelectedObj, getCandidatesSelectedId } from '@collections/candidates';
import { Attachment, AttachmentAdapter } from '@shared/models';
import { AttachmentService } from '@shared/services';
import { pdfFile, validUrl } from '@shared/utils';

import { CollectionsState, getCollectionsState } from '../collections.reducer';
import { attachmentsAdapter, AttachmentsState } from './attachments.reducer';

export const getAttachmentsState = createSelector(
  getCollectionsState,
  (state: CollectionsState) => !!state && state.attachments,
);

export const {
  selectIds: getAttachmentsIds,
  selectEntities: getAttachmentsEntities,
  selectAll,
  selectTotal: getAttachmentsTotal,
} = attachmentsAdapter.getSelectors(getAttachmentsState);

export const getAttachmentsAll = createSelector(selectAll, (attachments: Attachment[]) =>
  attachments
    .map((attachment: Attachment) => new AttachmentAdapter(attachment))
    .sort(
      (left: Attachment, right: Attachment) =>
        parseISO(right.createdDate).getTime() - parseISO(left.createdDate).getTime(),
    ),
);

export const getAttachmentsResumes = createSelector(
  getAttachmentsAll,
  (attachments: Attachment[], { candidateId }) =>
    !!attachments &&
    attachments.filter(
      (attachment: Attachment) =>
        !!attachment &&
        attachment.candidateId === candidateId &&
        attachment.associationType !== AttachmentService.AssociationType.COVER_LETTER &&
        attachment.associationType !== AttachmentService.AssociationType.COVER_LETTER_PDF,
    ),
);

export const getAttachmentsOthers = createSelector(
  getAttachmentsAll,
  (attachments: Attachment[]) =>
    !!attachments &&
    attachments.filter(
      (attachment: Attachment) =>
        !!attachment && attachment.associationType === AttachmentService.AssociationType.OTHERS,
    ),
);

export const getAttachmentsCoverLetters = createSelector(
  getAttachmentsAll,
  (attachments: Attachment[]) =>
    !!attachments &&
    attachments.filter(
      (attachment: Attachment) =>
        !!attachment &&
        attachment.associationType === AttachmentService.AssociationType.COVER_LETTER,
    ),
);

export const AttachmentObj = {
  getAttachmentsCoverLetters: (candidateId: number, isPdf: boolean) =>
    createSelector(getAttachmentsAll, (attachments: Attachment[]) => {
      const associationType = isPdf
        ? AttachmentService.AssociationType.COVER_LETTER_PDF
        : AttachmentService.AssociationType.COVER_LETTER;
      return (
        !!attachments &&
        attachments.filter(
          (attachment: Attachment) =>
            !!attachment &&
            attachment.candidateId === candidateId &&
            attachment.associationType === associationType,
        )
      );
    }),
};

export const getResumesLoaded = createSelector(
  getAttachmentsState,
  (state: AttachmentsState) => !!state && state.resumesLoaded,
);

export const getOthersLoaded = createSelector(
  getAttachmentsState,
  (state: AttachmentsState) => !!state && state.othersLoaded,
);

export const getNoteAttachmentState = createSelector(
  getAttachmentsState,
  (state: AttachmentsState) => !!state && state.noteAttachment,
);

export const getSelectedCandidateResumes = createSelector(
  getAttachmentsAll,
  getCandidatesSelectedId,
  (attachments: Attachment[], candidateId) =>
    attachments?.filter(
      (attachment: Attachment) =>
        !!attachment && attachment.candidateId?.toString() === candidateId?.toString(),
    ),
);

export const getSelectedCandidateLatestPdfResumePath = createSelector(
  getSelectedCandidateResumes,
  (resumes: Attachment[] = []) => {
    const validResumes: Attachment[] = resumes.filter(
      (resume) => validUrl.test(resume.path) && pdfFile.test(resume.name),
    );
    return validResumes[0]?.path || null;
  },
);

export const getSelectedCandidateLatestPdfResumeId = createSelector(
  getSelectedCandidateResumes,
  (resumes: Attachment[] = []) => {
    const validResumes: Attachment[] = resumes.filter(
      (resume) => validUrl.test(resume.path) && pdfFile.test(resume.name),
    );
    return validResumes[0]?.id || null;
  },
);

export const getAttachmentWithId = createSelector(
  getAttachmentsEntities,
  (entities, props) => props?.id && !!entities && entities[props.id],
);

export const defineIfPdfCantBeShown = createSelector(
  getSelectedCandidateLatestPdfResumePath,
  CandidateSelectedObj.getCandidatesSelected(true),
  (resumeSrc, candidate) => !resumeSrc || !candidate.isNotAnonymized,
);
